import { useMemo, type ForwardedRef, type ReactElement, useState } from "react";
import {
  type InputProps as ChakraInputProps,
  Input as ChakraInput,
  InputGroup,
  forwardRef,
  InputRightElement,
} from "@chakra-ui/react";
import { injectInputSize } from "./variant";
import type { InputSize, InputBreakPointSize } from "./type";
import { Icon } from "@/components/essentials/icon";
import { type FieldValues, type UseFormReset } from "react-hook-form";
import {
  inputTestId,
  chakraInputTestId,
  inputRightElementTestId,
  invisibleEyeSlashInputTestId,
  invisibleEyeInputTestId,
  timesCircleInputTestId,
} from "./const";
import { scrollOnFocus } from "@/modules/utils/scroll-on-focus";

export interface InputProps extends Omit<ChakraInputProps, "size"> {
  readonly size?: InputSize | InputBreakPointSize;
  readonly clear?: UseFormReset<FieldValues>;
  readonly invisible?: boolean;
}

export const Input = forwardRef<InputProps, "input">(
  (
    {
      _invalid = {
        boxShadow: "0 0 0 1px var(--chakra-colors-error-main)",
        bg: "error.light",
        _focus: {
          boxShadow: "0 0 0 1px var(--chakra-colors-error-main)",
          borderColor: "error.main",
          bg: "error.light",
        },
      },
      _focus = {
        boxShadow: "0 0 0 1px var(--chakra-colors-secondary-main)",
        borderColor: "secondary.main",
        bg: "light.main",
      },
      size = "md",
      type,
      clear,
      invisible,
      ...rest
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ): ReactElement => {
    const inputSize = useMemo(() => injectInputSize(size), [size]);
    const [invisibleState, setInvisibleState] = useState(invisible);

    return (
      <InputGroup display="flex" justifyContent="space-between" data-testid={inputTestId}>
        <ChakraInput
          ref={ref}
          errorBorderColor="error.main"
          focusBorderColor="secondary.main"
          bg="background.main"
          borderColor="background.main"
          borderWidth="0px"
          _invalid={_invalid}
          _placeholder={{ textColor: "gray.main" }}
          _hover={{}}
          _focus={_focus}
          type={invisibleState ? "password" : type}
          data-testid={chakraInputTestId}
          onFocus={scrollOnFocus}
          {...inputSize}
          {...rest}
        />
        {(clear !== undefined || invisible !== undefined) && (
          <InputRightElement
            display="flex"
            alignItems="center"
            right="1rem"
            gridGap="0.5rem"
            data-testid={inputRightElementTestId}
          >
            {invisible && (
              <Icon
                as={invisibleState ? "eyeSlash" : "eye"}
                onClick={() => {
                  setInvisibleState((state) => !state);
                }}
                data-testid={invisibleState ? invisibleEyeSlashInputTestId : invisibleEyeInputTestId}
              />
            )}
            {clear && (
              <Icon
                as="timesCircle"
                onClick={() => {
                  clear();
                }}
                data-testid={timesCircleInputTestId}
              />
            )}
          </InputRightElement>
        )}
      </InputGroup>
    );
  },
);
